<template>
  <component :is="tag" :class="{ fixedWidth, spEventDetail }" class="alignCenter eventCard">
    <template>
      <div class="eventsList-item-image">
        <custom-nuxt-link
          :to="eventPath"
          :class="{ larger: largerMode, noHeightSet: firstEvenLarger, spEventDetail }"
          class="eventCard-img"
          @click.native="sendOpixAndGTM"
        >
          <div v-if="imageFreeSize" class="imgFreeSize">
            <common-lazy-load-image :image-object="eventImage" :alt="eventImage.alt" background-color="unset" itemprop="image" />
          </div>
          <div v-else-if="spEventDetail" class="spEventDetail-wrap">
            <common-lazy-load-image :image-object="eventImage" :alt="eventImage.alt" itemprop="image" />
          </div>
          <common-lazy-load-image v-else :image-object="eventImage" :alt="eventImage.alt" itemprop="image" />
        </custom-nuxt-link>
        <div v-if="displayMjSelect(event)" class="eventsList-item-image-stamp">
          <common-lazy-load-image
            :image-url="'/img/stamp_mj-select.svg'"
            :image-object="{ width: 50, height: 50 }"
            :is-fixed-dimension="true"
            alt="mj-select"
            background-color="none"
          />
        </div>
      </div>
      <div :class="{ larger: firstEvenLarger }" class="eventCard-content">
        <fav-button v-if="!hideFavButton" :event="event" class="eventCard-fav" />
        <div class="eventCard-content-item">
          <icon far="fa-calendar-alt" /> <span :class="{ textSmall: spEventDetail }" class="eventCard-content-text">{{ startAt }}</span>
        </div>
        <div class="eventCard-content-item">
          <icon fas="fa-map-marker-alt" /> <span :class="{ textSmall: spEventDetail }" class="eventCard-content-text">{{ fullAreaName }}</span>
        </div>
      </div>
      <div v-if="rank" :class="`rank${rank}`" class="eventCard-rank">{{ rank }}</div>
    </template>
  </component>
</template>

<script>
const CommonLazyLoadImage = defineAsyncComponent(() => import('@/components/CommonLazyLoadImage.vue'))
import { timeConverter } from '@/utils/converter'
import { GA4_SELECT_ITEM } from '@/utils/constants/ga4'
import GTM from '@/utils/tracking/gtm'
import MjSelectMixin from '@/utils/mixins/MjSelectMixin'
const FavButton = defineAsyncComponent(() => import('@/components/FavButton.vue'))
const EventCardPc = defineAsyncComponent(() => import('./EventCardPc.vue'))
export default {
  components: { CommonLazyLoadImage, FavButton, EventCardPc },
  mixins: [MjSelectMixin],
  props: {
    tag: { type: String, required: false, default: 'div' },
    rank: { type: Number, required: false, default: null },
    event: { type: Object, required: true, default: null },
    hideFavButton: { type: Boolean, required: false, default: false },
    largerMode: { type: Boolean, required: false, default: false },
    listPosition: { type: Number, required: false, default: 0 },
    firstLarger: { type: Boolean, required: false, default: false },
    fixedWidth: { type: Boolean, required: false, default: true },
    recommend: { type: String, required: false, default: '' },
    imageFreeSize: { type: Boolean, required: false, default: false },
    spEventDetail: { type: Boolean, required: false, default: false },
    from: { type: String, required: false, default: '' }
  },
  computed: {
    eventImage() {
      return this.event.eyecatch_image || this.event.eyecatch
    },
    startAt() {
      return timeConverter(this.event.start_at).fullDateWithWeekDay
    },
    fullAreaName() {
      return this.event.full_area_name ? this.event.full_area_name : this.event.full_area_name_for_front
    },
    eventPath() {
      return this.recommend ? `/events/${this.event.code}?${this.recommend}` : `/events/${this.event.code}`
    },
    firstEvenLarger() {
      return this.largerMode && this.firstLarger && this.listPosition === 1
    }
  },
  methods: {
    sendOpixAndGTM() {
      this.sendOpixClickedEvent()
      this.pushGtmData()
    },
    sendOpixClickedEvent() {
      const data = {
        path: `/events/${this.event.code}`,
        showedBlock: this.from
      }
      this.$emit(`click-from-${this.from}`, data)
    },
    plusBookmarkedCount() {
      this.$emit('plus-bookmark-count')
    },
    minusBookmarkedCount() {
      this.$emit('minus-bookmark-count')
    },
    pushGtmData() {
      GTM.sendEcommerceDataToGa(GA4_SELECT_ITEM, this.event, this.$store.myGender)
    }
  }
}
</script>

<style lang="scss" scoped>
.eventCard {
  @include clearAfter;
  display: inline-block;
  position: relative;
  border: 1px solid $gray-c6;
  border-radius: 3px;
  &.spEventDetail {
    width: 238px;
    height: 162px;
    margin-right: 10px;
  }
  .spEventDetail {
    &-wrap {
      height: 107px;
    }
  }
  &.fixedWidth {
    width: 146px;
    margin-right: 10px;
    @include pc {
      width: 198px;
    }
  }
  &-img {
    img {
      width: auto;
      height: 64px;
      @include pc {
        height: 88px;
      }
    }
    &.spEventDetail {
      img {
        height: inherit;
        width: 100%;
        object-fit: contain;
        object-position: top;
      }
    }
    &.larger {
      img {
        width: 100%;
        height: 88px;
      }
      .imgFreeSize {
        height: 88px;
        @media screen and (max-width: 350px) {
          height: 80px;
        }
        img {
          height: inherit;
          width: 100%;
          object-fit: contain;
          object-position: top;
        }
      }
    }
    &.noHeightSet {
      img {
        height: auto;
      }
    }
  }
  &-content {
    text-align: left;
    padding: 0 0.5rem 0.5rem;
    font-size: 1rem;
    @include pc {
      font-size: 1.2rem;
      padding: 0.5rem 1rem 0.5rem;
    }
    &-item {
      @include textEllipsis;
    }
    &.larger {
      font-size: 1.5rem;
      padding: 0.5rem 1rem 0.5rem;
    }
  }
  &-fav {
    float: right;
    margin: 0.2rem 0.5rem 0.5rem 0;
    @include pc {
      margin: -0.5rem 0 0.5rem 0;
    }
  }
  &-rank {
    @include flex;
    position: absolute;
    top: -1px;
    left: -1px;
    width: 3rem;
    height: 3rem;
    border: 2px solid $gray-66;
    background-color: $white;
    color: $gray-66;
    font-size: 16px;
    font-weight: bold;
    line-height: 1;
    pointer-events: none;
    @include pc {
      top: -6px;
      left: 5px;
    }
    &.rank1 {
      background: $gold-medal;
    }
    &.rank2 {
      background: $gray-80;
      color: $white;
    }
    &.rank3 {
      background: $bronze-medal;
      color: $white;
    }
  }
}

.eventsList-item-image {
  position: relative;
  &-stamp {
    position: absolute;
    bottom: 10px;
    left: 4px;
    width: 50px;
    height: 50px;
  }
}
</style>
